import { Monitor, Smartphone, Download } from "lucide-react";
import { toast } from "sonner";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { useLocale } from "@/hooks/useLocale";

/**
 * Homepage section with two boxes -- Desktop Shortcut and Mobile App --
 * from the mockup review. Both are wired to the real, already-existing
 * useInstallPrompt hook (the same one behind the header's "Create
 * Shortcut" button), not new/duplicated install logic.
 *
 * Handled honestly per platform rather than assuming one button works
 * everywhere:
 * - Chrome/Edge (desktop or Android): a real native install prompt fires
 * - iOS Safari: there's no programmatic install API (Apple doesn't
 *   support it), so this shows the actual manual steps instead of a
 *   button that would silently do nothing
 * - Already installed, or no automated install path available yet
 *   (canPrompt false, not iOS): the whole section hides. An earlier
 *   version showed a fallback message here pointing at "the install
 *   icon in your address bar" -- for the site-wide root-scope
 *   manifest this section installs, that control usually does exist,
 *   but hiding when it's genuinely not ready yet is still the more
 *   honest default than guessing at browser UI that may or may not be
 *   there for a given visitor.
 */
export function InstallSection() {
  const { canPrompt, isInstalled, isIOS, promptInstall } = useInstallPrompt();
  const { strings } = useLocale();

  if (isInstalled) return null;
  if (!canPrompt && !isIOS) return null;

  const handleDesktopClick = async () => {
    if (!canPrompt) {
      toast.info(strings.install.desktopUnavailableTitle, {
        description: strings.install.desktopUnavailableDesc,
      });
      return;
    }
    const outcome = await promptInstall();
    if (outcome === "accepted") {
      toast.success(strings.install.desktopSuccess);
    }
  };

  const handleMobileClick = async () => {
    if (isIOS) {
      toast.info(strings.install.addToHomeScreenTitle, {
        description: strings.install.iosInstructions,
        duration: 9000,
      });
      return;
    }
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === "accepted") {
        toast.success(strings.install.mobileSuccess);
      }
      return;
    }
    toast.info(strings.install.addToHomeScreenTitle, {
      description: strings.install.genericInstructions,
    });
  };

  return (
    <section className="container py-10">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
        {/* Desktop Shortcut hidden below the sm breakpoint: on a real
            mobile viewport this action has no working install path
            (canPrompt is false there and it isn't iOS), so the button
            would only ever show the "not available" toast -- dead weight
            for a mobile visitor, and one less thing competing for
            attention on a small screen. Shown from sm: up (tablet and
            desktop), where the native install prompt genuinely works. */}
        <button
          onClick={handleDesktopClick}
          className="hidden sm:flex items-center gap-4 p-5 rounded-xl border border-primary/20 bg-primary/5 hover:border-primary/40 hover:bg-primary/10 transition-colors text-left"
        >
          <div className="w-11 h-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <Monitor className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">{strings.install.desktopTitle}</p>
            <p className="text-xs text-muted-foreground">{strings.install.desktopSubtitle}</p>
          </div>
          <Download className="h-4 w-4 text-primary shrink-0" />
        </button>

        <button
          onClick={handleMobileClick}
          className="flex items-center gap-4 p-5 rounded-xl border border-primary/20 bg-primary/5 hover:border-primary/40 hover:bg-primary/10 transition-colors text-left"
        >
          <div className="w-11 h-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">{strings.install.mobileTitle}</p>
            <p className="text-xs text-muted-foreground">{strings.install.mobileSubtitle}</p>
          </div>
          <Download className="h-4 w-4 text-primary shrink-0" />
        </button>
      </div>
    </section>
  );
}
